import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import CollegeImagesService from "../../Services/CollegeImagesService";
import toast from "react-hot-toast";
import AddCollegeImage from "./AddCollegeImage";
import EditCollegeImage from "./EditCollegeImage";

function MainCollegeImage({ showModal, handleCloseModal, collegeId }) {
  const [collegeImages, setCollegeImages] = useState([]);
  const [addCollegeImageModal, setAddCollegeImageModal] = useState(false);
  const [editCollegeImageModal, setEditCollegeImageModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const getCollegeImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await CollegeImagesService.getCollegeImages(collegeId);
      if (response.data.success === true) {
        setCollegeImages(response.data.data);
      } else {
        setCollegeImages([]);
      }
    } catch (error) {
      toast.error(error);
      setCollegeImages([]);
    } finally {
      setLoading(false);
    }
  }, [collegeId]);

  useEffect(() => {
    getCollegeImages();
  }, [getCollegeImages]);

  const handleDeleteImage = async () => {
    try {
      const response = await CollegeImagesService.deleteCollegeImages(
        imageToDelete.id
      );
      if (response.data.success === true) {
        toast.success("Image deleted successfully");
        setDeleteConfirmModal(false);
        setImageToDelete(null);
        getCollegeImages(); // Refresh the images list
      } else {
        toast.error(response.data.message || "Failed to delete image");
      }
    } catch (error) {
      toast.error("Error deleting image: " + error.message);
    }
  };

  const openDeleteConfirm = (image) => {
    setImageToDelete(image);
    setDeleteConfirmModal(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmModal(false);
    setImageToDelete(null);
  };

  return (
    <>
      <Modal show={showModal} onHide={handleCloseModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Main College Image</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <Row xs={1} md={3} className="g-3">
            {loading ? (
              <Col
                className="d-flex flex-column justify-content-center align-items-center w-100"
                style={{ minHeight: "150px" }}
              >
                <Spinner animation="border" variant="primary" />
                <p
                  className="text-center mt-2 mb-0 text-muted"
                  style={{ fontSize: "1.1rem" }}
                >
                  Loading images...
                </p>
              </Col>
            ) : collegeImages.length <= 0 ? (
              <Col
                className="d-flex flex-column justify-content-center align-items-center w-100"
                style={{ minHeight: "150px" }}
              >
                <i
                  className="bi bi-image"
                  style={{ fontSize: "2.5rem", color: "#adb5bd" }}
                ></i>
                <p
                  className="text-center mt-2 mb-0 text-muted"
                  style={{ fontSize: "1.1rem" }}
                >
                  No images found
                </p>
              </Col>
            ) : (
              collegeImages.map((image) => (
                <Col
                  key={image.id}
                  className="d-flex flex-column align-items-center"
                >
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "4/3",
                      overflow: "hidden",
                      borderRadius: "12px",
                      border: "1px solid #dee2e6",
                      background: "#f8f9fa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "0.5rem",
                      position: "relative",
                    }}
                  >
                    <img
                      src={image.imagePath}
                      alt={image.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "12px",
                        transition: "transform 0.2s",
                      }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        borderRadius: "50%",
                        width: "32px",
                        height: "32px",
                        padding: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                      onClick={() => openDeleteConfirm(image)}
                      title="Delete Image"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </Col>
              ))
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>

          {collegeImages.length <= 0 ? (
            <Button
              variant="primary"
              onClick={() => setAddCollegeImageModal(true)}
              disabled={loading}
            >
              Add Campus Images
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setEditCollegeImageModal(true)}
              disabled={loading}
            >
              Edit College Images
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {addCollegeImageModal && (
        <AddCollegeImage
          showModal={addCollegeImageModal}
          handleCloseModal={() => setAddCollegeImageModal(false)}
          collegeId={collegeId}
          onSuccess={getCollegeImages}
        />
      )}

      {editCollegeImageModal && (
        <EditCollegeImage
          showModal={editCollegeImageModal}
          handleCloseModal={() => setEditCollegeImageModal(false)}
          collegeId={collegeId}
          onSuccess={getCollegeImages}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={deleteConfirmModal} onHide={closeDeleteConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this image?</p>
          {imageToDelete && (
            <div className="text-center">
              <img
                src={imageToDelete.imagePath}
                alt={imageToDelete.name}
                style={{
                  width: "100px",
                  height: "75px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}
              />
              <p
                className="mt-2 mb-0 text-muted"
                style={{ fontSize: "0.9rem" }}
              >
                {imageToDelete.name}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteConfirm}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteImage}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default MainCollegeImage;
